export const CalendarConnectionVisibility = Object.freeze({
  PRIVATE: 'private',
  COMPANY_WIDE: 'company_wide'
})

export const CalendarBookingCapableDrivers = Object.freeze({
  CAL_COM: 'calcom',
  CALENDLY: 'calendly'
})

export const CALENDAR_DRIVER_LABELS = Object.freeze({
  [CalendarBookingCapableDrivers.CAL_COM]: 'Cal.com',
  [CalendarBookingCapableDrivers.CALENDLY]: 'Calendly',
  google_calendar: 'Google Calendar',
  outlook: 'Outlook'
})

export const CalendarBookingStatus = Object.freeze({
  SCHEDULED: 'scheduled',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  NO_SHOW: 'no_show'
})

export const CALENDAR_BOOKING_STATUS_VARIANTS = Object.freeze({
  [CalendarBookingStatus.SCHEDULED]: 'success',
  [CalendarBookingStatus.CONFIRMED]: 'success',
  [CalendarBookingStatus.CANCELLED]: 'danger',
  [CalendarBookingStatus.COMPLETED]: 'secondary',
  [CalendarBookingStatus.NO_SHOW]: 'warning'
})

export const CALENDAR_BOOKING_STATUS_LABELS = Object.freeze({
  [CalendarBookingStatus.SCHEDULED]: 'Scheduled',
  [CalendarBookingStatus.CONFIRMED]: 'Confirmed',
  [CalendarBookingStatus.CANCELLED]: 'Cancelled',
  [CalendarBookingStatus.COMPLETED]: 'Completed',
  [CalendarBookingStatus.NO_SHOW]: 'No Show'
})

// Discriminator used to tell a CalendarV2 booking feed item apart from a real
// Communication or a ContactAudit inside contact.mixin's communicationsAndAudits.
export const CALENDAR_V2_BOOKING_SOURCE = 'calendar_v2_booking'

// Discriminator for a CalendarV2 booking activity log (reschedule/cancel history)
// flattened into its own feed item inside contact.mixin's communicationsAndAudits.
export const CALENDAR_V2_ACTIVITY_LOG_SOURCE = 'calendar_v2_activity_log'

export const CalendarBookingActivityLogAction = Object.freeze({
  RESCHEDULED: 'rescheduled',
  CANCELLED: 'cancelled'
})

export const CalendarBookingActivityLogSource = Object.freeze({
  AGENT: 'agent',
  EXTERNAL_WEBHOOK: 'external_webhook'
})

// $VueEvent names used to keep the contact timeline in sync with CalendarV2 bookings
// without a refetch, mirroring the new_communication/update_communication/delete_communication
// socket-driven pattern already used for real communications.
export const CalendarV2BookingEvents = Object.freeze({
  CREATED: 'calendar_v2_booking_created',
  UPDATED: 'calendar_v2_booking_updated',
  CANCELLED: 'calendar_v2_booking_cancelled',
  EDIT_REQUESTED: 'calendar_v2_booking_edit_requested'
})

export function resolveDriverIconUrl (icon) {
  if (!icon) {
    return ''
  }

  return icon.startsWith('http') ? icon : `${process.env.API_URL}${icon}`
}

export function getCalendarConnectionAccountLabel (connection) {
  return (
    connection?.connected_account?.email ||
    connection?.connected_account?.username ||
    connection?.connected_account?.label ||
    connection?.title
  )
}

export function isCompanyWideCalendarConnection (connection) {
  return connection?.visibility === CalendarConnectionVisibility.COMPANY_WIDE
}

export function getCalendarConnectionProviderAccountLabel (connection) {
  return `${connection.driver_label} — ${getCalendarConnectionAccountLabel(
    connection
  )}`
}

export function getCalendarConnectionVisibilityLabel (connection) {
  return isCompanyWideCalendarConnection(connection)
    ? 'Company calendar'
    : 'Private'
}

export function getCalendarConnectionEventTypeLabel (connection) {
  return connection?.selected_target_label || connection?.driver_label
}

// AI-739. Approved copy, used verbatim on every surface: the Talk Calendar tab, the Talk
// External Calendars settings page, the Classic Integrations page and the AloAi modal.
// Do not reword, and do not add a deprecation date — the decommission is data-driven.
export const CALENDAR_RECOMMENDATION_HEADING =
  'A better way to book: we recommend connecting Calendly or Cal.com'

export const CALENDAR_RECOMMENDATION_BODY =
  "Over time, these will become the main way to book appointments in Aloware. Nothing changes for your current calendar today, and we'll give you plenty of notice before anything does. Connect whenever you're ready."

export const CALENDAR_RECOMMENDATION_LEARN_MORE_URL =
  'https://support.aloware.com/en/articles/16498975-external-calendars'

// Matches SettingsMap['external_calendars'].route.
export const EXTERNAL_CALENDARS_SETTINGS_ROUTE = '/settings/external-calendars'

// Written onto the landing URL by api-core's OAuth callback (CallbackController). A redirect
// replaced the popup's postMessage, which cannot work: both providers serve a
// Cross-Origin-Opener-Policy document during the flow, so window.opener is null by the time the
// callback runs, and popup.closed reads true with the window still open.
export const OAUTH_RETURN_CONNECTION_PARAMETER = 'calendar_connection_id'

export const OAUTH_RETURN_ERROR_PARAMETER = 'calendar_connection_error'
